import React, { useMemo, useCallback, useState, useRef } from "react";
import DataTableWrapper from "../../../Components/CommonTableLayout/DataTableWrapper";
import { Link } from "react-router-dom";
import { fetchOutboundOrders, fetchFilterOptions } from "../../../services/OutboundOrders/OutboundOrderTableServices";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {
  getInitialFilterModel,
  createColumnFilterChangeHandler,
  createCascadingFilterParams,
  createDateFilterParams,
  sanitizeRootFilters
} from "../../../utils/columnFilterUtils";
import { Box } from "@mui/material";
import { Filter3Sharp } from "@mui/icons-material";
import { formatValue } from "../../../utils/currencyUtils";

const DEFAULT_PAGE_SIZE = 25;
const FILTER_CACHE_KEY = 'demand_order';


const OutboundOrderTable = ({ filters, setRowCount, onColumnFilterChanged }) => {
  const [currentFilters, setCurrentFilters] = useState({});

  // Get initial filter model from localStorage
  const initialFilterModel = useMemo(() => getInitialFilterModel(FILTER_CACHE_KEY), []);
  const fetchCacheRef = useRef(new Map());
  const debounceTimersRef = useRef({});

  // Create filter change handler
  const handleColumnFilterChanged = useCallback((columnFilters, rawFilterModel) => {
    try {
      // Save enriched AG Grid model for UI restoration
      if (rawFilterModel) {
        const enrichedRaw = Object.fromEntries(
          Object.entries(rawFilterModel || {}).map(([field, model]) => {
            let filterType = model?.filterType;
            if (!filterType) {
              if (Array.isArray(model?.values)) {
                filterType = 'set';
              } else if (model?.type) {
                const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);
                filterType = dateTypes.has(model.type) ? 'date' : 'text';
              } else {
                filterType = 'unknown';
              }
            }
            return [field, { ...model, filterType }];
          })
        );
        localStorage.setItem(FILTER_CACHE_KEY, JSON.stringify(enrichedRaw));
      }
    } catch (error) {
      console.error('Error saving filters:', error);
    }

    setCurrentFilters(columnFilters);

    if (typeof onColumnFilterChanged === 'function') {
      onColumnFilterChanged(columnFilters);
    }

    try {
      const pageKey = FILTER_CACHE_KEY;
      const simplifiedWithTypes = Object.fromEntries(
        Object.entries(columnFilters || {}).map(([field, value]) => {
          let filterType = undefined;
          const raw = rawFilterModel?.[field];
          if (raw?.filterType) {
            filterType = raw.filterType;
          } else if (Array.isArray(raw?.values)) {
            filterType = 'set';
          } else if (raw?.type) {
            const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);
            filterType = dateTypes.has(raw.type) ? 'date' : 'text';
          }
          return [field, { filterType, values: value }];
        })
      );
      localStorage.setItem(`columnFilters_${pageKey}`, JSON.stringify(simplifiedWithTypes));
      console.log('columnFilters', `columnFiltersChanged_${pageKey}`)
      window.dispatchEvent(new CustomEvent(`columnFiltersChanged_${pageKey}`));
    } catch (err) {
      console.warn('Failed to persist/notify column filter change', err);
    }
  }, [onColumnFilterChanged]);

  // Strict cascading filter mapping (filterType + values)
  const mapFiltersForApi = useCallback((columnFilters) => {
    const mapped = { columnFilters: {} };
    Object.entries(columnFilters || {}).forEach(([key, filterObj]) => {
      if (key !== 'state' && key !== 'exceptionType') {

        // CASE 1: Stored format { filterType, values }
        if (filterObj?.filterType === 'date') {

          mapped.columnFilters[key] = {
            filterType: 'date',
            ...filterObj.values
          };
        }

        else if (filterObj?.filterType) {
          // Keep set/text as-is
          mapped.columnFilters[key] = {
            filterType: filterObj.filterType,
            values: filterObj.values
          };
        }

        else if (filterObj?.type) {
          const dateTypes = new Set(['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank']);

          if (dateTypes.has(filterObj.type)) {

            mapped.columnFilters[key] = {
              filterType: 'date',
              ...filterObj
            }
          } else {
            mapped.columnFilters[key] = {
              filterType: 'text',
              values: filterObj
            };
          }
        }

        // CASE 3: Set array
        else if (Array.isArray(filterObj)) {
          mapped.columnFilters[key] = {
            filterType: 'set',
            values: filterObj
          };
        }

      }
    });
    if (filters?.states && filters.states.length > 0) {
      mapped.states = filters.states;
    }
    if (filters?.exceptions && filters.exceptions.length > 0) {
      mapped.exceptions = filters.exceptions;
    }
    return mapped;
  }, [filters]);

  const fetchOptionsStable = useCallback((col, columnFilters) => {
    const apiPayload = mapFiltersForApi(columnFilters);
    const key = `${col}::${JSON.stringify(apiPayload)}`;
    if (fetchCacheRef.current.has(key)) {
      return fetchCacheRef.current.get(key);
    }

    let resolveFn, rejectFn;
    const p = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });
    fetchCacheRef.current.set(key, p);

    if (debounceTimersRef.current[key]) clearTimeout(debounceTimersRef.current[key]);
    debounceTimersRef.current[key] = setTimeout(async () => {
      try {
        const res = await fetchFilterOptions(col, apiPayload);
        resolveFn(res);
      } catch (err) {
        fetchCacheRef.current.delete(key);
        rejectFn(err);
      } finally {
        delete debounceTimersRef.current[key];
      }
    }, 120);

    return p;
  }, [mapFiltersForApi]);


  const columns = useMemo(
    () => {

      return [
        {
          field: "orderNo",
          headerName: "Order No",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'orderNo',
            currentFilters
          ),

          cellRenderer: (params) => {
            let borderColor = "transparent";
            switch (params.data?.exceptionType) {
              case "Past Due": borderColor = "#fbc02d"; break;
              case "Late": borderColor = "#ff560e"; break;
              case "Unconfirmed": borderColor = "#bc6bd9"; break;
              case "On Time": borderColor = "#268000"; break;
              default: borderColor = "black";
            }
            return (
              <div style={{ borderLeft: `5px solid ${borderColor}`, paddingLeft: 8, display: "flex", alignItems: "center" }}>
                <LocalShippingOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                <Link
                  to={"order-detail/" + params.value}
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                  state={{ menu: "Demand", label: "Sales Orders", outerLabel: "Sales Orders Detail" }}
                >
                  {params.value}
                </Link>
              </div>
            );
          },
        },
        {
          field: "customer",
          headerName: "Customer",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'customer',
            currentFilters
          )
        },
        {
          field: "customerName", headerName: "Sold To Party", filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'customerName',
            currentFilters
          )
        },
        {
          field: "createdBy",
          headerName: "Created By",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'createdBy',
            currentFilters
          )
        },
        {
          field: "supplier",
          headerName: "Supplier",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'supplier',
            currentFilters
          )
        },
        {
          field: "supplierName", headerName: "Supplier Name", filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'supplierName',
            currentFilters
          )
        },
        {
          field: "shipTo",
          headerName: "Ship To",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'shipTo',
            currentFilters
          )
        },
        {
          field: "shipToSite", headerName: "Ship To Site", filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'shipToSite',
            currentFilters
          )
        },
        {
          field: "shipToOwner",
          headerName: "Ship To Owner",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'shipToOwner',
            currentFilters
          )
        },
        {
          field: "billTo", headerName: "Bill To", filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'billTo',
            currentFilters
          )
        },
        {
          field: "billToDescription",
          headerName: "Bill To Description",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'billToDescription',
            currentFilters
          )
        },
        {
          field: "soldTo",
          headerName: "Sold To",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'soldTo',
            currentFilters
          )
        },
        {
          field: "soldToDescription",
          headerName: "Sold To Description",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'soldToDescription',
            currentFilters
          )
        },
        {
          field: "creationDate",
          headerName: "Creation Date",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
        {
          field: "lmd",
          headerName: "Last Modified Date",
          filter: 'agDateColumnFilter',
          filterParams: createDateFilterParams()
        },
        {
          field: "orderType",
          headerName: "Order Type",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'orderType',
            currentFilters
          )
        },
        {
          field: "processType",
          headerName: "Process Type",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'processType',
            currentFilters
          )
        },
        {
          field: "erpOrderType",
          headerName: "ERP Order Type",
          filter: 'agSetColumnFilter',
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'erpOrderType',
            currentFilters
          )
        },
        {
          field: "totalWeightOrder", headerName: "Total Weight Order", filter: 'agSetColumnFilter',
          valueFormatter: (params) => formatValue(params.value),
          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'totalWeightOrder',

            currentFilters
          )
        },
        {
          field: "totalWeightUom", headerName: "Total Weight UOM", filter: 'agSetColumnFilter',

          filterParams: createCascadingFilterParams(
            (col, filtersForCol) => fetchOptionsStable(col, filtersForCol),
            'totalWeightUom',
            currentFilters
          )
        },
      ];
    },
    [currentFilters, fetchOptionsStable]
  );

  const fetchRows = useCallback(
    async ({ page, size, sort }) => {
      try {
        // Validate column filters to prevent cross-screen contamination
        const validatedFilters = sanitizeRootFilters(filters, columns);

        const { rows, totalElements } = await fetchOutboundOrders({
          page,
          size,
          sort,
          filters: validatedFilters || {},
        });

        if (setRowCount) setRowCount(totalElements);

        return { rows, totalElements };
      } catch (err) {
        return { rows: [], totalElements: 0 };
      }
    },
    [filters, setRowCount, columns]
  );

  // const appliedFilters = useMemo(
  //   () => [
  //     {
  //       label: "Type A",
  //       value: "typeA",
  //       onRemove: () => console.log("remove Type A"),
  //     },
  //     {
  //       label: "Type B",
  //       value: "typeB",
  //       onRemove: () => console.log("remove Type B"),
  //     },
  //   ],
  //   []
  // );

  return (
    <Box sx={{
      height: "calc(100vh - 255px)",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
      boxSizing: "border-box",
      pb: 1
    }}>
      <DataTableWrapper
        title="Sales Orders"
        columns={columns}
        filters={filters}
        height={500}
        fetchRows={fetchRows}
        rowCount={undefined}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        onColumnFilterChanged={handleColumnFilterChanged}
        initialFilterModel={initialFilterModel}
      />
    </Box>
  );
};

export default OutboundOrderTable;
